import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useItems } from '../hooks/useItems';
import { useUrlFilters } from '../hooks/useUrlFilters';
import { applyFilters } from '../lib/filters/applyFilters';
import { FilterToolbar } from '../components/FilterToolbar';
import { GroupingToggle } from '../components/GroupingToggle';
import { ItemDrawer } from '../components/ItemDrawer';
import type { Item } from '@mas-flow/types';
import type { GroupBy } from '../lib/filters/types';

const DECISION_ORDER: string[] = ['Inbox', 'Now', 'Next', 'Later', 'Parked'];
const LIFECYCLE_ORDER: string[] = ['Submitted', 'InTriage', 'Scored', 'Decided', 'Done'];
const BAND_ORDER: string[] = ['High', 'Medium', 'Low', 'Unscored'];

function groupKey(it: Item, group: GroupBy): string {
  switch (group) {
    case 'decision': return it.decision ?? 'Inbox';
    case 'lifecycle': return it.lifecycle;
    case 'priorityBand': return it.priorityBandOverride ?? it.priorityBand ?? 'Unscored';
    case 'category': return it.categoryId;
  }
}

function sectionOrder(group: GroupBy, keys: string[]): string[] {
  const known = group === 'decision' ? DECISION_ORDER : group === 'lifecycle' ? LIFECYCLE_ORDER : group === 'priorityBand' ? BAND_ORDER : [];
  const set = new Set(keys);
  const ordered = known.filter((k) => set.has(k));
  const remainder = keys.filter((k) => !known.includes(k)).sort();
  return [...ordered, ...remainder];
}

export function DashboardPage() {
  const { user } = useAuth();
  const { filters, setFilters } = useUrlFilters();
  const { data: itemsAll } = useItems(filters.showArchived);
  const navigate = useNavigate();
  const userId = user?.userId ?? '_anon';
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // NOTE: keep this a plain `??` chain (like PortfolioPage). The previous
  // IIFE + `||` version triggered an esbuild (<=0.25) minifier bug that
  // emitted `a ?? b || c` — a SyntaxError that blanked the entire app.
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(`kanbanGroupBy:${userId}`) : null;
  const group: GroupBy = filters.group ?? (stored as GroupBy | null) ?? 'decision';

  const filtered = useMemo(() => applyFilters(itemsAll ?? [], filters), [itemsAll, filters]);
  const sorted = useMemo(() => [...filtered].sort((a, b) => (b.wsjf ?? -1) - (a.wsjf ?? -1)), [filtered]);

  const sections = useMemo(() => {
    const buckets = new Map<string, Item[]>();
    sorted.forEach((it) => {
      const k = groupKey(it, group);
      const arr = buckets.get(k) ?? [];
      arr.push(it);
      buckets.set(k, arr);
    });
    return sectionOrder(group, [...buckets.keys()]).map((k) => ({ key: k, items: buckets.get(k)! }));
  }, [sorted, group]);

  const setGroup = (g: GroupBy) => {
    try { localStorage.setItem(`kanbanGroupBy:${userId}`, g); } catch { /* ignore */ }
    setFilters({ ...filters, group: g });
  };
  const toggleSection = (k: string) => {
    const next = new Set(collapsed);
    if (next.has(k)) next.delete(k); else next.add(k);
    setCollapsed(next);
  };

  return (
    <div className="flex flex-col gap-3 dashboard">
      <div className="dashboard-toolbar flex items-center gap-3">
        <h1 className="text-xl font-semibold">Dashboard</h1>
        <span className="text-sm text-slate-500">{sorted.length} items</span>
        <button type="button" onClick={() => window.print()} className="ml-auto rounded border border-slate-300 bg-white px-2 py-1 text-xs">Export PDF</button>
      </div>
      <div className="dashboard-toolbar"><FilterToolbar filters={filters} onChange={setFilters} /></div>
      <div className="dashboard-toolbar"><GroupingToggle value={group} onChange={setGroup} /></div>
      <div className="flex flex-col gap-4">
        {sections.map((s, sectionIndex) => (
          <section key={s.key} className={`dashboard-section ${sectionIndex > 0 ? 'page-break' : ''} rounded border border-slate-200 bg-white`}>
            <header
              onClick={() => toggleSection(s.key)}
              className="sticky top-0 z-10 cursor-pointer border-b border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium"
            >
              {s.key} ({s.items.length}) {collapsed.has(s.key) ? '▸' : '▾'}
            </header>
            {!collapsed.has(s.key) && (
              <ol className="divide-y divide-slate-100">
                {s.items.map((it, idx) => (
                  <li
                    key={it.id}
                    onClick={() => navigate({ search: `?item=${it.id}` })}
                    className="flex cursor-pointer items-baseline gap-4 px-3 py-3 hover:bg-slate-50"
                  >
                    <span className="w-10 text-2xl font-light tabular-nums text-slate-400">{(idx + 1).toString().padStart(2, '0')}</span>
                    <div className="flex-1">
                      <div className="text-base font-semibold text-slate-900">
                        {it.title}
                        {it.isAnchor && <span className="ml-1 text-amber-600" title="anchor">⚓</span>}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                        <span className="rounded bg-slate-100 px-1.5 py-0.5">BV {it.scores.businessValue ?? '—'}</span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5">TC {it.scores.timeCriticality ?? '—'}</span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5">RR {it.scores.riskOpportunity ?? '—'}</span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5">Size {it.scores.jobSize ?? '—'}</span>
                        {it.wsjf != null && <span className="rounded bg-brand-wash px-1.5 py-0.5 font-medium text-brand-dark">WSJF {it.wsjf.toFixed(1)}</span>}
                        {it.decision && <span className="rounded bg-green-100 px-1.5 py-0.5 text-green-800">{it.decision}</span>}
                        {it.confidence && <span>· {it.confidence} confidence</span>}
                        <span className="text-slate-400">· {it.categoryId} · {it.owner?.displayName ?? '—'}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        ))}
      </div>
      <ItemDrawer />
    </div>
  );
}
